import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { PersonBadge } from '@/components/common/PersonBadge';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { specimenStatementQueryOptions } from '../api/zonalStatement.api';
import { getSpecimenStatementColumns } from '../components/StatementColumns';
import { STATEMENT_PERSON_ROLE_LABEL } from '../constants/zonalStatement.constants';
import { getSpecimenStatCards } from '../utils/statementStatCards';

const route = getRouteApi('/_auth/dashboards/zonal-statement/$userId/specimen');

const STATEMENT_NAME = 'Specimen Statement';

interface SpecimenStatementPageProps {
  userId: number;
}

export function SpecimenStatementPage({ userId }: SpecimenStatementPageProps) {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  useRememberViewedRow('zonalStatementZms', userId);

  const statementQuery = useQuery(
    specimenStatementQueryOptions(userId, { dateFrom: search.dateFrom, dateTo: search.dateTo }),
  );
  const statement = statementQuery.data;
  const totals = statement?.totals;
  const columns = useMemo(() => getSpecimenStatementColumns(totals), [totals]);

  return (
    <DetailTableLayout
      title={STATEMENT_NAME}
      breadcrumbAddon={
        <PersonBadge
          roleLabel={STATEMENT_PERSON_ROLE_LABEL}
          name={statement?.member.name}
          isLoading={statementQuery.isLoading}
        />
      }
      titleAddon={
        <DateRangeBadge
          dateFrom={search.dateFrom ?? statement?.period.dateFrom}
          dateTo={search.dateTo ?? statement?.period.dateTo}
        />
      }
      onNoHistory={() => void navigate({ to: '/dashboards/zonal-statement', search })}
      stats={getSpecimenStatCards(statement)}
      table={{
        rows: statement?.items ?? [],
        columns,
        isLoading: statementQuery.isLoading,
        error: statementQuery.error,
        errorTitle: 'Could not load the specimen statement',
        onRetry: () => void statementQuery.refetch(),
        stickyColumnId: 'name',
        emptyMessage: 'No specimen is flagged for closing in this period',
        emptyHint: DETAIL_TABLE_WIDER_DATES_HINT,
      }}
    />
  );
}
